import { env } from '../config/env'

export type RealtimeChannel = 'activity' | 'conversation'

export type ClientFrame =
  | { type: 'auth'; token: string }
  | { type: 'subscribe'; channel: RealtimeChannel; id: string }
  | { type: 'unsubscribe'; channel: RealtimeChannel; id: string }
  | { type: 'typing'; channel: 'conversation'; id: string; typing: boolean }
  | { type: 'ping' }

export type ServerFrame =
  | { type: 'ready'; userId: string; serverTime: string }
  | { type: 'activity.message'; activityId: string; message: unknown }
  | {
      type: 'activity.participant'
      activityId: string
      participant: unknown
      event: string
    }
  | { type: 'private.message'; conversationId: string; message: unknown }
  | { type: 'notification'; notification: unknown }
  | { type: 'join_request'; activityId: string; request: unknown }
  | {
      type: 'typing'
      conversationId: string
      userId: string
      typing: boolean
    }
  | { type: 'error'; code: string; message: string }
  | { type: 'pong' }

export type RealtimeStatus =
  | 'idle'
  | 'connecting'
  | 'authenticating'
  | 'ready'
  | 'reconnecting'
  | 'closed'

export interface RealtimeSocket {
  readyState: number
  send(data: string): void
  close(code?: number, reason?: string): void
  onopen: ((event: Event) => void) | null
  onclose: ((event: CloseEvent) => void) | null
  onerror: ((event: Event) => void) | null
  onmessage: ((event: MessageEvent) => void) | null
}

export type RealtimeSocketFactory = (url: string) => RealtimeSocket

export interface RealtimeClientOptions {
  url?: string
  getAccessToken: () => string | null
  onFrame?: (frame: ServerFrame) => void
  onStatus?: (status: RealtimeStatus) => void
  onAuthError?: () => void
  socketFactory?: RealtimeSocketFactory
  autoReconnect?: boolean
  reconnectBaseMs?: number
  reconnectMaxMs?: number
  authTimeoutMs?: number
  heartbeatMs?: number
  random?: () => number
  maxSubscriptions?: number
}

/** Frozen close codes and the retryable subset (REALTIME.md §2, §11). */
export const REALTIME_CLOSE_UNAUTHORIZED = 4401
export const REALTIME_CLOSE_TOO_MANY_CONNECTIONS = 4429
export const REALTIME_CLOSE_GOING_AWAY = 1001
export const REALTIME_CLOSE_TRY_AGAIN_LATER = 1013

const RETRYABLE_CLOSE_CODES = new Set([
  1001, // going away: server restart/HAProxy handoff
  1005, // no status received (browser reports the abnormal close as 1006)
  1006, // abnormal closure: network loss
  1011, // internal server error
  1012, // service restart
  1013, // try again later (slow consumer)
  1014, // bad gateway
  REALTIME_CLOSE_TOO_MANY_CONNECTIONS,
])

/**
 * Auth closes never retry automatically: the session/token must be refreshed
 * or cleared first. Everything else is retried only when the close code is in
 * the documented retryable set.
 */
export function isRetryableCloseCode(code: number): boolean {
  return RETRYABLE_CLOSE_CODES.has(code)
}

const AUTH_ERROR_CODES = new Set([
  'UNAUTHORIZED',
  'TOKEN_EXPIRED',
  'TOKEN_INVALID',
  'TOKEN_VERSION_MISMATCH',
  'EMAIL_NOT_VERIFIED',
  'SESSION_REVOKED',
])

const OPEN = 1
const MAX_SUBSCRIPTIONS = 50

/** Resolves the configured/derived WebSocket URL, including relative values. */
export function resolveRealtimeUrl(value: string | undefined): string {
  if (value && /^wss?:\/\//i.test(value)) return value
  if (typeof window === 'undefined') return value || 'ws://localhost/ws'
  const protocol = window.location.protocol === 'https:' ? 'wss:' : 'ws:'
  const path = value?.trim() || '/ws'
  if (path.startsWith('/')) {
    return `${protocol}//${window.location.host}${path}`
  }
  return path
}

function defaultUrl(): string {
  return resolveRealtimeUrl(env.wsUrl)
}

/**
 * Realtime transport for `/ws`.
 *
 * The browser cannot set Authorization headers on WebSocket, so every
 * connection sends the first-frame `auth` message within 5 seconds. Auth
 * closes (4401) leave the socket closed; retryable closes (1001, 1013, 4429,
 * network errors) reconnect with capped exponential backoff and the client
 * re-subscribes. Feature code must refetch its route data when status returns
 * to `ready`, because Pub/Sub delivery is best effort (ARCHITECTURE §7).
 */
export class RealtimeClient {
  private readonly url: string
  private readonly getAccessToken: () => string | null
  private readonly onFrame?: (frame: ServerFrame) => void
  private readonly onStatus?: (status: RealtimeStatus) => void
  private readonly onAuthError?: () => void
  private readonly socketFactory: RealtimeSocketFactory
  private readonly autoReconnect: boolean
  private readonly reconnectBaseMs: number
  private readonly reconnectMaxMs: number
  private readonly authTimeoutMs: number
  private readonly heartbeatMs: number
  private readonly random: () => number
  private readonly maxSubscriptions: number

  private socket: RealtimeSocket | null = null
  private status: RealtimeStatus = 'idle'
  private closedByUser = false
  private authTimer: ReturnType<typeof setTimeout> | null = null
  private heartbeatTimer: ReturnType<typeof setInterval> | null = null
  private reconnectTimer: ReturnType<typeof setTimeout> | null = null
  private reconnectAttempt = 0
  private authFailureNotified = false
  private readonly subscriptions = new Set<string>()

  constructor(options: RealtimeClientOptions) {
    this.url = options.url ?? defaultUrl()
    this.getAccessToken = options.getAccessToken
    this.onFrame = options.onFrame
    this.onStatus = options.onStatus
    this.onAuthError = options.onAuthError
    this.socketFactory =
      options.socketFactory ?? ((url) => new WebSocket(url))
    this.autoReconnect = options.autoReconnect ?? true
    this.reconnectBaseMs = options.reconnectBaseMs ?? 500
    this.reconnectMaxMs = options.reconnectMaxMs ?? 30_000
    this.authTimeoutMs = options.authTimeoutMs ?? 5_000
    this.heartbeatMs = options.heartbeatMs ?? 30_000
    this.random = options.random ?? Math.random
    this.maxSubscriptions = options.maxSubscriptions ?? MAX_SUBSCRIPTIONS
  }

  getStatus(): RealtimeStatus {
    return this.status
  }

  connect(): void {
    if (
      this.socket &&
      (this.socket.readyState === 0 || this.socket.readyState === OPEN)
    ) {
      return
    }

    this.closedByUser = false
    this.authFailureNotified = false
    this.clearReconnectTimer()
    this.setStatus('connecting')

    let socket: RealtimeSocket
    try {
      socket = this.socketFactory(this.url)
    } catch {
      this.scheduleReconnect()
      return
    }
    this.socket = socket

    socket.onopen = () => {
      if (this.socket !== socket) return
      const token = this.getAccessToken()
      if (!token) {
        this.handleAuthFailure()
        this.closeSocket(socket, REALTIME_CLOSE_UNAUTHORIZED, 'missing token')
        return
      }
      this.setStatus('authenticating')
      this.send({ type: 'auth', token })
      this.authTimer = setTimeout(() => {
        if (this.socket !== socket) return
        this.handleAuthFailure()
        this.closeSocket(socket, REALTIME_CLOSE_UNAUTHORIZED, 'auth timeout')
      }, this.authTimeoutMs)
    }

    socket.onmessage = (event) => {
      if (this.socket !== socket) return
      const frame = parseServerFrame(event.data)
      if (!frame) return

      if (frame.type === 'error' && isAuthErrorCode(frame.code)) {
        // The hub normally closes with 4401 as well; closing here keeps the
        // client from continuing to use an invalid token when it does not.
        this.handleAuthFailure()
        this.closeSocket(socket, REALTIME_CLOSE_UNAUTHORIZED, frame.code)
      }

      if (frame.type === 'ready') {
        this.clearAuthTimer()
        this.reconnectAttempt = 0
        // Re-subscribe before announcing readiness so resync listeners refetch
        // with the channels already restored.
        this.resubscribe()
        this.startHeartbeat()
        this.setStatus('ready')
      }
      this.onFrame?.(frame)
    }

    socket.onerror = () => {
      // The close handler owns reconnect and status transitions.
    }

    socket.onclose = (event) => {
      if (this.socket !== socket) return
      this.clearAuthTimer()
      this.stopHeartbeat()
      this.socket = null

      if (event.code === REALTIME_CLOSE_UNAUTHORIZED) {
        this.reconnectAttempt = 0
        this.handleAuthFailure()
        this.setStatus('closed')
        return
      }
      if (this.closedByUser) {
        this.setStatus('closed')
        return
      }
      if (!this.autoReconnect || !isRetryableCloseCode(event.code)) {
        this.reconnectAttempt = 0
        this.setStatus('closed')
        return
      }
      this.scheduleReconnect()
    }
  }

  /**
   * Stops the transport. Subscriptions are retained by default so a manual
   * reconnect keeps the current screen channels; logout calls
   * `disconnect({ clearSubscriptions: true })`.
   */
  disconnect(options: { clearSubscriptions?: boolean } = {}): void {
    this.closedByUser = true
    this.clearTimers()
    const socket = this.socket
    this.socket = null
    socket?.close(1000, 'client disconnect')
    if (options.clearSubscriptions) this.subscriptions.clear()
    this.reconnectAttempt = 0
    this.setStatus('closed')
  }

  subscribe(channel: RealtimeChannel, id: string): void {
    if (!channel || !id) return
    const key = `${channel}:${id}`
    const alreadyTracked = this.subscriptions.has(key)
    if (!alreadyTracked && this.subscriptions.size >= this.maxSubscriptions) {
      // The server also rejects the 51st channel; never send an avoidable
      // protocol error that would spam the console.
      return
    }
    this.subscriptions.add(key)
    if (!alreadyTracked && this.status === 'ready') {
      this.send({ type: 'subscribe', channel, id })
    }
  }

  unsubscribe(channel: RealtimeChannel, id: string): void {
    const key = `${channel}:${id}`
    if (!this.subscriptions.delete(key)) return
    if (this.status === 'ready') {
      this.send({ type: 'unsubscribe', channel, id })
    }
  }

  setTyping(id: string, typing: boolean): void {
    if (this.status !== 'ready') return
    this.send({ type: 'typing', channel: 'conversation', id, typing })
  }

  private resubscribe(): void {
    for (const key of this.subscriptions) {
      const separator = key.indexOf(':')
      const channel = key.slice(0, separator) as RealtimeChannel
      const id = key.slice(separator + 1)
      this.send({ type: 'subscribe', channel, id })
    }
  }

  private send(frame: ClientFrame): void {
    if (!this.socket || this.socket.readyState !== OPEN) return
    try {
      this.socket.send(JSON.stringify(frame))
    } catch {
      // A close event follows; never log frame contents (tokens, messages).
    }
  }

  private setStatus(status: RealtimeStatus): void {
    if (this.status === status) return
    this.status = status
    this.onStatus?.(status)
  }

  private handleAuthFailure(): void {
    this.clearAuthTimer()
    if (this.authFailureNotified) return
    this.authFailureNotified = true
    this.onAuthError?.()
  }

  private closeSocket(
    socket: RealtimeSocket,
    code: number,
    reason: string,
  ): void {
    try {
      socket.close(code, reason)
    } catch {
      // The close event still follows or the socket is already gone.
    }
  }

  private scheduleReconnect(): void {
    if (!this.autoReconnect || this.closedByUser) {
      this.setStatus('closed')
      return
    }
    const exponential = Math.min(
      this.reconnectMaxMs,
      this.reconnectBaseMs * 2 ** this.reconnectAttempt,
    )
    const jitter = Math.floor(this.random() * exponential * 0.25)
    const delay = Math.min(this.reconnectMaxMs, exponential + jitter)
    this.reconnectAttempt += 1
    this.setStatus('reconnecting')
    this.clearReconnectTimer()
    this.reconnectTimer = setTimeout(() => {
      this.reconnectTimer = null
      this.connect()
    }, delay)
  }

  private startHeartbeat(): void {
    this.stopHeartbeat()
    this.heartbeatTimer = setInterval(() => {
      this.send({ type: 'ping' })
    }, this.heartbeatMs)
  }

  private stopHeartbeat(): void {
    if (this.heartbeatTimer) {
      clearInterval(this.heartbeatTimer)
      this.heartbeatTimer = null
    }
  }

  private clearAuthTimer(): void {
    if (this.authTimer) {
      clearTimeout(this.authTimer)
      this.authTimer = null
    }
  }

  private clearReconnectTimer(): void {
    if (this.reconnectTimer) {
      clearTimeout(this.reconnectTimer)
      this.reconnectTimer = null
    }
  }

  private clearTimers(): void {
    this.clearAuthTimer()
    this.clearReconnectTimer()
    this.stopHeartbeat()
  }
}

function isAuthErrorCode(code: string): boolean {
  return AUTH_ERROR_CODES.has(code.trim().toUpperCase())
}

/**
 * Parses the JSON frame and reconciles the one documented naming difference:
 * REALTIME.md publishes `private.typing`, while the frozen client schema
 * exposes `typing` (API_CONTRACT §10 and ARCHITECTURE §7).
 */
export function parseServerFrame(data: unknown): ServerFrame | null {
  let parsed: unknown
  try {
    parsed = typeof data === 'string' ? JSON.parse(data) : null
  } catch {
    return null
  }
  if (!parsed || typeof parsed !== 'object') return null
  const frame = parsed as Record<string, unknown>
  if (typeof frame.type !== 'string') return null

  if (frame.type === 'private.typing') {
    return {
      type: 'typing',
      conversationId: String(frame.conversationId ?? ''),
      userId: String(frame.userId ?? ''),
      typing: Boolean(frame.typing),
    }
  }

  return parsed as ServerFrame
}
